import { Comment } from "@/components/comments/Comment.tsx";
import { FormResultForEmployeeProps } from "@/components/form-result/result-modal/components/types.ts";
import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { QuestionElementProps } from "@/components/questionnaire/components/element.type.ts";
import { getFormKey } from "@/components/questionnaire/questionnaire.types.ts";
import { Box, Button, Stack, Text, Title, HtmlViewer } from "@skailer-com/uikit";

import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const EmployeeNPSElement = ({ element }: QuestionElementProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const scaleOptions = Array.from({ length: 11 }, (_, i) => i);
  const employee_nps_settings = element.employee_nps_settings;
  const { t } = useTranslation();
  const formElementName = getFormKey(element);
  const hasError = !!errors[formElementName];

  if (!employee_nps_settings) return null;
  return (
    <Stack gap={"2xl"} data-error={hasError || undefined}>
      <Stack gap="md">
        <Title
          header={employee_nps_settings.title || t("surveys:employee_nps_element_title_default")}
          size={"md"}
          required={employee_nps_settings.is_required}
        />
        <HtmlViewer
          content={
            employee_nps_settings.description ||
            t("surveys:employee_nps_element_description_default")
          }
        />
      </Stack>

      <Controller
        name={formElementName}
        control={control}
        render={({ field }) => (
          <Stack>
            <Stack direction={"horizontal"} className={"justify-between"} gap={"xs"}>
              {scaleOptions.map((value) => (
                <Button
                  className={"grow px-1"}
                  size={"lg"}
                  key={value}
                  variant={
                    field.value && field.value.toString() === value.toString()
                      ? "primary"
                      : "secondary"
                  }
                  onClick={() => field.onChange(value.toString())}
                >
                  {value}
                </Button>
              ))}
            </Stack>
            <div className="mt-4">
              <div className="flex justify-between text-sm text-secondary">
                <span>{t("surveys:employee_nps_not_at_all_likely")}</span>
                <span>{t("surveys:employee_nps_extremely_likely")}</span>
              </div>
            </div>
            {errors[formElementName]?.message && (
              <Box className={"pt-2 pb-4"}>
                <Text className={"text-danger"}>{String(errors[formElementName]?.message)}</Text>
              </Box>
            )}
          </Stack>
        )}
      />

      {employee_nps_settings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};

export const EmployeeNPSElementResult = ({ element, answer }: FormResultForEmployeeProps) => {
  const scaleOptions = Array.from({ length: 11 }, (_, i) => i);
  const employee_nps_settings = element.employee_nps_settings;
  const { t } = useTranslation();

  if (!employee_nps_settings) return null;

  return (
    <>
      <Title
        header={employee_nps_settings.title || t("surveys:employee_nps_element_title_default")}
        size={"sm"}
        paddingTop={true}
      />
      <Stack gap={"md"} className={"mt-3"}>
        <Stack direction={"horizontal"} className={"justify-between"} gap={"xs"}>
          {scaleOptions.map((value) => (
            <Button
              className={"grow px-1 flex-1"}
              size={"lg"}
              key={value}
              variant={
                answer?.answer_int?.toString() === value.toString() ? "primary" : "secondary"
              }
            >
              {value}
            </Button>
          ))}
        </Stack>
        {answer && answer.comment && (
          <Comment text={answer.comment} employee={null} isShort={false} />
        )}
      </Stack>
    </>
  );
};
